import React, { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { validateImageFile } from "../../utils/formatters";

interface FileUploadProps {
  accept?: string[];
  maxSize?: number; // in MB
  onFileSelect: (file: File | null) => void;
  label: string;
  required?: boolean;
  currentFile?: File | string | null;
  error?: string;
  slice?: false | number;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  accept = ["image/jpeg", "image/jpg", "image/png"],
  maxSize = 5,
  onFileSelect,
  label,
  required,
  currentFile,
  error,
  slice = false,
}) => {
  const [, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string>("");
  const [previewUrl, setPreviewUrl] = useState<string>("");

  // Cleanup object URL on component unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Cleanup object URL when component unmounts or file changes
  useEffect(() => {
    if (currentFile) {
      if (currentFile instanceof File) {
        try {
          const url = URL.createObjectURL(currentFile);
          setPreviewUrl(url);

          return () => {
            URL.revokeObjectURL(url);
          };
        } catch (error) {
          console.error("Error creating object URL:", error);
          setPreviewUrl("");
        }
      } else if (typeof currentFile === "string") {
        setPreviewUrl(currentFile);
      }
    } else {
      setPreviewUrl("");
    }
  }, [currentFile]);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      setDragActive(false);
      setValidationError("");

      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (
          rejection.errors.some((error: any) => error.code === "file-too-large")
        ) {
          setValidationError(`File size must be less than ${maxSize}MB`);
        } else if (
          rejection.errors.some(
            (error: any) => error.code === "file-invalid-type",
          )
        ) {
          setValidationError("Please select a valid image file (JPG, PNG)");
        }
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const validation = validateImageFile(file, maxSize);

        if (validation.isValid) {
          onFileSelect(file);
        } else {
          console.log("Invalid file", validation);
          setValidationError(validation.error || "Invalid file");
          onFileSelect(null);
        }
      }
    },
    [maxSize, onFileSelect],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: accept.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
    maxSize: maxSize * 1024 * 1024,
    multiple: false,
  });

  const handleRemoveFile = () => {
    onFileSelect(null);
    setValidationError("");
  };

  const displayError = error || validationError;

  return (
    <div className={"form-field" + (required ? "required" : "")}>
      <label>{label}</label>

      <div className="file-input-wrapper">
        {!currentFile ? (
          <div
            {...getRootProps()}
            className={`file-input-label ${
              isDragActive ? "border-primary-500 bg-primary-50" : ""
            }`}
          >
            <input {...getInputProps()} />
            <div className="text-center">
              <div className="text-lg mb-2">{isDragActive ? "📁" : "📤"}</div>
              <div className="font-medium">
                {isDragActive
                  ? "Drop your file here"
                  : "Choose image file or drag and drop"}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                JPG, PNG - Max {maxSize}MB
              </div>
            </div>
          </div>
        ) : (
          <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start space-x-4 flex-1">
                {/* Image Preview */}
                <div className="w-20 h-20 bg-white rounded-lg border-2 border-green-200 overflow-hidden flex-shrink-0">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  {currentFile instanceof File ? (
                    <>
                      <div className="font-medium text-green-800 truncate">
                        {slice ? currentFile.name.slice(0, slice).concat("...") : currentFile.name}
                      </div>
                      <div className="text-sm text-green-600">
                        {(currentFile.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </>
                  ) : (
                    <div className="font-medium text-green-800 truncate">
                      Image selected
                    </div>
                  )}
                  <div className="flex items-center mt-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    <span className="text-xs text-green-600">
                      Ready to upload
                    </span>
                  </div>
                </div>
              </div>

              {/* Remove Button */}
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-red-500 hover:text-red-700 text-lg font-bold hover:bg-red-50 rounded-full w-8 h-8 flex items-center justify-center transition-colors duration-200 flex-shrink-0"
                title="Remove file"
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>

      {displayError && (
        <div className="validation-message error mt-1">
          <span className="validation-icon">✗</span>
          <span>{displayError}</span>
        </div>
      )}
    </div>
  );
};
